import { useMemo, useState } from "react";

import dayjs, { type Dayjs } from "dayjs";
import { useForm } from "react-hook-form";

import Button from "@/components/Button";
import Input from "@/components/Input/Input";
import useUserStore from "@/store/userStore";
import type { Vehicle } from "@/types/vehicleReservation";
import { hasTimeConflict } from "@/utils/vehicleAvailability";

interface ExistingReservation {
  drivingTime: string;
  status: string;
}

interface VehicleReservationFormProps {
  date: Dayjs;
  vehicle?: Vehicle | null;
  existingReservations?: ExistingReservation[];
  onSubmit: (data: VehicleFormValues) => void;
}

export interface VehicleFormValues {
  drivingTime: string;
  purpose: string;
  destination: string;
  driverName: string;
  driverPhone: string;
}

const TIME_OPTIONS = Array.from({ length: 25 }, (_, i) => {
  const h = String(i).padStart(2, "0");

  return { value: `${h}:00`, label: `${h}:00` };
});

const FormLabel = ({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) => (
  <label className="text-small font-medium text-primary">
    {children}
    {required && <span className="text-red"> *</span>}
  </label>
);

const VehicleReservationForm = ({
  date,
  vehicle,
  existingReservations = [],
  onSubmit,
}: VehicleReservationFormProps) => {
  const { userName, telNo, cmtNm } = useUserStore();

  const isToday = date.isSame(dayjs(), "day");
  const minHour = isToday ? Math.min(dayjs().hour() + 1, 23) : 0;
  const defaultStart = `${String(Math.max(minHour, 9)).padStart(2, "0")}:00`;
  const defaultEndIdx =
    TIME_OPTIONS.findIndex(t => t.value === defaultStart) + 1;
  const defaultEnd = TIME_OPTIONS[defaultEndIdx]?.value ?? "24:00";

  // 전일 예약은 이 차량에 그날 예약이 전혀 없을 때만 가능 → 가능하면 전일을 기본 선택
  const isAllDayAvailable = existingReservations.length === 0;
  const [isAllDay, setIsAllDay] = useState(isAllDayAvailable);
  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);

  const startOptions = useMemo(
    () =>
      TIME_OPTIONS.slice(0, -1).filter(t => {
        const h = parseInt(t.value);

        return h >= minHour;
      }),
    [minHour],
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<VehicleFormValues>({
    mode: "onChange",
    defaultValues: {
      drivingTime: isAllDayAvailable ? "전일" : `${defaultStart} ~ ${defaultEnd}`,
      purpose: "",
      destination: "",
      driverName: userName || "",
      driverPhone: telNo || "",
    },
  });

  // 선택한 시간대가 이 차량의 기존 예약과 겹치지 않는지
  const isTimeRangeAvailable = useMemo(
    () => !hasTimeConflict(existingReservations, `${startTime} ~ ${endTime}`),
    [existingReservations, startTime, endTime],
  );

  const updateDrivingTime = (start: string, end: string, allDay: boolean) => {
    const time = allDay ? "전일" : `${start} ~ ${end}`;
    setValue("drivingTime", time, { shouldValidate: true });
  };

  const handleAllDayToggle = () => {
    const next = !isAllDay;
    setIsAllDay(next);
    if (next) {
      setStartTime("00:00");
      setEndTime("24:00");
      setValue("drivingTime", "전일", { shouldValidate: true });
    } else {
      updateDrivingTime(startTime, endTime, false);
    }
  };

  const handleStartChange = (val: string) => {
    setStartTime(val);
    if (val >= endTime) {
      const nextIdx = TIME_OPTIONS.findIndex(t => t.value === val) + 1;
      const newEnd = TIME_OPTIONS[nextIdx]?.value ?? "24:00";
      setEndTime(newEnd);
      updateDrivingTime(val, newEnd, false);
    } else {
      updateDrivingTime(val, endTime, false);
    }
  };

  const handleEndChange = (val: string) => {
    setEndTime(val);
    updateDrivingTime(startTime, val, false);
  };

  const endOptions = TIME_OPTIONS.filter(t => t.value > startTime);

  return (
    <div className="flex flex-col gap-4">
      {/* Date header */}
      <div className="text-lg font-semibold text-primary">
        {date.format("YYYY년 M월 D일 (dd)")}
      </div>

      {/* Available badge */}
      <div>
        <span className="inline-block rounded-full border border-green-500 px-3 py-1 text-small font-medium text-green-500">
          예약 가능
        </span>
      </div>

      {/* Vehicle info */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="pb-2 text-small font-medium text-primary">차량 정보</p>
        <div className="flex flex-col gap-1.5 text-small">
          <div className="flex gap-2">
            <span className="min-w-[100px] text-gray-dull">차량 종류</span>
            <span>{vehicle?.vehicleName || "-"}</span>
          </div>
          <div className="flex gap-2">
            <span className="min-w-[100px] text-gray-dull">차량 번호</span>
            <span>{vehicle?.vehicleNo || "-"}</span>
          </div>
        </div>
      </div>

      {/* Applicant info (auto-filled) */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="pb-2 text-small font-medium text-primary">신청자 정보</p>
        <div className="flex flex-col gap-1.5 text-small">
          <div className="flex gap-2">
            <span className="min-w-[100px] text-gray-dull">공동체</span>
            <span>{cmtNm || "-"}</span>
          </div>
          <div className="flex gap-2">
            <span className="min-w-[100px] text-gray-dull">신청자</span>
            <span>{userName}</span>
          </div>
          <div className="flex gap-2">
            <span className="min-w-[100px] text-gray-dull">연락처</span>
            <span>{telNo || "-"}</span>
          </div>
        </div>
      </div>

      {/* Form */}
      <form className="flex flex-col gap-3" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2">
          <FormLabel required>운행시간</FormLabel>
          <label
            className={`flex items-center gap-2 ${isAllDayAvailable ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
          >
            <input
              type="checkbox"
              checked={isAllDay}
              onChange={handleAllDayToggle}
              disabled={!isAllDayAvailable}
              className="accent-primary"
            />
            <span className="text-small text-gray-dark">전일</span>
          </label>
          {!isAllDayAvailable && (
            <p className="text-xs text-gray-400">
              이 차량은 이미 예약이 있어 전일 예약은 불가합니다. 시간대 예약만
              가능합니다.
            </p>
          )}
          {!isAllDay && (
            <div className="flex items-center gap-2">
              <select
                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                value={startTime}
                onChange={e => handleStartChange(e.target.value)}
              >
                {startOptions.map(t => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-400">~</span>
              <select
                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                value={endTime}
                onChange={e => handleEndChange(e.target.value)}
              >
                {endOptions.map(t => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <input
            type="hidden"
            {...register("drivingTime", { required: true })}
          />
          {!isAllDay && !isTimeRangeAvailable && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs">
              <p className="font-medium text-red-700">
                이 차량은 선택한 시간대에 이미 예약이 있습니다. 다른 시간대를
                선택해주세요.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <FormLabel required>운행목적</FormLabel>
          <Input
            placeholder="차량 사용 목적을 입력하세요"
            {...register("purpose", { required: true })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <FormLabel required>목적지</FormLabel>
          <Input
            placeholder="이동 목적지를 입력하세요"
            {...register("destination", { required: true })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <FormLabel required>운전자</FormLabel>
          <Input
            placeholder="실제 운전자 이름"
            {...register("driverName", { required: true })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <FormLabel required>운전자 연락처</FormLabel>
          <Input
            placeholder="010-0000-0000"
            {...register("driverPhone", { required: true })}
          />
        </div>

        <div className="flex justify-center pt-2">
          <Button
            type="submit"
            variant="primary"
            disabled={
              !isValid ||
              (isAllDay ? !isAllDayAvailable : !isTimeRangeAvailable)
            }
          >
            신청하기
          </Button>
        </div>
      </form>
    </div>
  );
};

export default VehicleReservationForm;
